import { useState } from 'react';
import { Activity, Loader2, Sparkles, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 7 physical attributes — must match backend PHYSICAL_ATTRIBUTES
const PHYSICAL_ATTRS = [
  'Speed', 'Acceleration', 'Agility', 'Coordination',
  'Balance', 'Explosiveness', 'Endurance',
];

// Map UI display -> backend skill key (lowercase snake)
const ATTR_TO_KEY = {
  'Speed': 'speed',
  'Acceleration': 'acceleration',
  'Agility': 'agility',
  'Coordination': 'coordination',
  'Balance': 'balance',
  'Explosiveness': 'explosiveness',
  'Endurance': 'endurance',
};

const LEVEL_COLORS = {
  Focus: 'bg-red-500/15 text-red-300 border-red-500/30',
  Developing: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Proficient: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

/**
 * Performance Coach AI Assistant — physical-only LLM helper.
 * Generates level + score + observation + next-step for the 7 physical
 * attributes plus a coach-ready narrative paragraph.
 *
 * Props mirror AIEvaluationAssistant for swap-in compatibility but the
 * onApplySuggestions handler receives data already keyed by lowercase
 * physical skill names (speed, acceleration, ...) for direct use in the
 * techDev state.
 */
const PerformanceAIAssistant = ({
  athleteName,
  athletePosition,
  athleteAge,
  onApplySuggestions,
  getAuthHeaders,
}) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [form, setForm] = useState({
    age: athleteAge || '',
    sex: '',
    position: athletePosition || 'MID',
    level_context: 'Competitive',
    physical_level: 'On Track',
    speed_profile: '',
    acceleration_quality: '',
    movement_efficiency: '',
    physical_stability: '',
    endurance_state: '',
    top_strengths_keywords: '',  // comma-separated in UI
    main_focus_keywords: '',
    coach_observations: '',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        top_strengths_keywords: form.top_strengths_keywords
          .split(',').map((s) => s.trim()).filter(Boolean).slice(0, 3),
        main_focus_keywords: form.main_focus_keywords
          .split(',').map((s) => s.trim()).filter(Boolean).slice(0, 3),
      };
      const { data } = await axios.post(
        `${API}/ai/generate-physical-evaluation`,
        payload,
        { headers: getAuthHeaders() }
      );
      setSuggestions(data);
      setShowForm(false);
      toast.success('Physical evaluation generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  const applyAll = () => {
    if (!suggestions) return;
    // Match the existing handleApplyAISuggestions shape:
    //   { physical: { speed: { level, score, note } } }
    const physical = {};
    for (const attr of PHYSICAL_ATTRS) {
      const skill = suggestions.skills?.[attr];
      if (!skill) continue;
      physical[ATTR_TO_KEY[attr]] = {
        level: skill.level,
        score: skill.value,
        note: skill.observation || '',
      };
    }
    onApplySuggestions({ physical });
    toast.success('Applied to evaluation form');
    setSuggestions(null);
    setShowForm(true);
  };

  return (
    <div className="rounded-lg border border-orange-400/30 bg-orange-400/5 p-4" data-testid="performance-ai-assistant">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="text-orange-300" size={18} />
          <h3 className="font-heading text-sm uppercase tracking-wider text-orange-200">
            Physical AI Assistant
          </h3>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="text-orange-300 hover:text-orange-200 p-1"
          data-testid="performance-ai-toggle"
        >
          {showForm ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {showForm && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Tell the assistant what you observe. It will suggest levels, scores, and next steps for {athleteName || 'this athlete'}.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input
              type="number"
              placeholder="Age"
              value={form.age}
              onChange={(e) => update('age', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-age"
            />
            <select
              value={form.position}
              onChange={(e) => update('position', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-position"
            >
              <option>GK</option><option>DEF</option><option>MID</option><option>FWD</option>
            </select>
            <select
              value={form.level_context}
              onChange={(e) => update('level_context', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-level"
            >
              <option>Recreational</option><option>Competitive</option><option>Academy</option>
            </select>
            <select
              value={form.physical_level}
              onChange={(e) => update('physical_level', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-snapshot"
            >
              <option>Below</option><option>On Track</option><option>Above</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Speed profile (e.g. 'Quick over 5m')"
              value={form.speed_profile}
              onChange={(e) => update('speed_profile', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-speed"
            />
            <input
              type="text"
              placeholder="Acceleration quality"
              value={form.acceleration_quality}
              onChange={(e) => update('acceleration_quality', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-accel"
            />
            <input
              type="text"
              placeholder="Movement efficiency (agility/coord)"
              value={form.movement_efficiency}
              onChange={(e) => update('movement_efficiency', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-move"
            />
            <input
              type="text"
              placeholder="Balance / stability"
              value={form.physical_stability}
              onChange={(e) => update('physical_stability', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs"
              data-testid="phys-ai-balance"
            />
            <input
              type="text"
              placeholder="Endurance state"
              value={form.endurance_state}
              onChange={(e) => update('endurance_state', e.target.value)}
              className="bg-zinc-900/60 border border-border/40 rounded-md px-2 py-1.5 text-xs sm:col-span-2"
              data-testid="phys-ai-endurance"
            />
          </div>

          <Textarea
            placeholder="Free-form observations (optional)"
            value={form.coach_observations}
            onChange={(e) => update('coach_observations', e.target.value)}
            rows={2}
            className="bg-zinc-900/60 border-border/40 text-xs"
            data-testid="phys-ai-notes"
          />

          <Button
            onClick={generate}
            disabled={loading}
            className="bg-orange-400/25 hover:bg-orange-400/40 text-orange-100 border border-orange-400/40 w-full"
            data-testid="phys-ai-generate"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Generate Physical Evaluation</>
            )}
          </Button>
        </div>
      )}

      {suggestions && !showForm && (
        <div className="space-y-3 mt-3" data-testid="phys-ai-suggestions">
          {suggestions.summary_line && (
            <div className="bg-zinc-900/60 border border-orange-400/20 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-widest text-orange-300 mb-1">Summary</p>
              <p className="text-sm">{suggestions.summary_line}</p>
            </div>
          )}

          {suggestions.physical_narrative && (
            <div className="bg-zinc-900/60 border border-border/40 rounded-md p-3" data-testid="phys-ai-narrative">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Physical Narrative</p>
              <p className="text-xs leading-relaxed">{suggestions.physical_narrative}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PHYSICAL_ATTRS.map((attr) => {
              const skill = suggestions.skills?.[attr];
              if (!skill) return null;
              return (
                <div key={attr} className="bg-zinc-900/60 border border-border/40 rounded-md p-2.5" data-testid={`phys-ai-skill-${ATTR_TO_KEY[attr]}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold">{attr}</span>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${LEVEL_COLORS[skill.level] || ''}`}>
                      {skill.level} · {skill.value?.toFixed(1)}
                    </span>
                  </div>
                  {skill.observation && (
                    <p className="text-[11px] text-muted-foreground mb-1">{skill.observation}</p>
                  )}
                  {skill.next_step && (
                    <p className="text-[11px] text-orange-200/80">→ {skill.next_step}</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              onClick={applyAll}
              className="bg-orange-400/30 hover:bg-orange-400/50 text-orange-100 border border-orange-400/40 flex-1"
              data-testid="phys-ai-apply"
            >
              <Check className="mr-2 h-4 w-4" /> Apply to Evaluation
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setSuggestions(null); setShowForm(true); }}
              className="text-muted-foreground"
              data-testid="phys-ai-discard"
            >
              <X className="mr-2 h-4 w-4" /> Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceAIAssistant;
